// API Service
// Provides utility functions for making authenticated API calls

import { getToken } from './authService';
import { getServer } from './serverConfig';

const TENANT_ID = 'newa';
const API_VERSION = '2.0';

// Get default headers for authenticated requests
const getAuthHeaders = (additionalHeaders = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    'accept': 'application/json',
  };

  // Merge additional headers, allowing them to override defaults
  // Handle case-insensitive header keys (Accept vs accept)
  Object.keys(additionalHeaders).forEach(key => {
    // Remove any existing header with same key (case-insensitive for Accept/accept)
    if (key.toLowerCase() === 'accept') {
      delete headers['accept'];
      delete headers['Accept'];
    }
    if (key.toLowerCase() === 'content-type') {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
    headers[key] = additionalHeaders[key];
  });

  if (token) {
    headers['Authorization'] = token; // No "Bearer " prefix as per API requirements
    headers['X-Tenant-Id'] = TENANT_ID;
    headers['X-Api-Version'] = API_VERSION;
  }

  return headers;
};

// Make authenticated GET request
export const apiGet = async (endpoint, options = {}) => {
  try {
    const API_BASE_URL = getServer();
    const headers = getAuthHeaders(options.headers);
    
    // For GET requests, remove Content-Type if Accept: */* is specified
    // Some APIs (like /asset/asset-all) don't need Content-Type
    if (headers['Accept'] === '*/*' || headers['accept'] === '*/*') {
      delete headers['Content-Type'];
    }
    
    // Extract headers from options to avoid overriding
    const { headers: _, ...restOptions } = options;
    
    // Debug logging
    console.log(`[apiGet] ${endpoint}`);
    console.log('[apiGet] Headers:', headers);
    console.log('[apiGet] Full URL:', `${API_BASE_URL}${endpoint}`);
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: headers,
      ...restOptions,
    });
    
    console.log(`[apiGet] Response status: ${response.status}`);

    if (response.status === 401) {
      // Unauthorized - token might be expired
      // Could trigger logout here
      throw new Error('Unauthorized - please login again');
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `API request failed with status ${response.status}`);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API GET error:', error);
    return { success: false, error: error.message };
  }
};

// Make authenticated POST request
export const apiPost = async (endpoint, body, options = {}) => {
  try {
    const API_BASE_URL = getServer();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(options.headers),
      body: JSON.stringify(body),
      ...options,
    });

    if (response.status === 401) {
      // Unauthorized - token might be expired
      throw new Error('Unauthorized - please login again');
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `API request failed with status ${response.status}`);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API POST error:', error);
    return { success: false, error: error.message };
  }
};

// Make authenticated multipart/form-data POST request
export const apiPostFormData = async (endpoint, formData, options = {}) => {
  try {
    const API_BASE_URL = getServer();
    const headers = getAuthHeaders(options.headers);
    delete headers['Content-Type'];
    delete headers['content-type'];

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
      ...options,
    });

    if (response.status === 401) {
      throw new Error('Unauthorized - please login again');
    }

    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : await response.text();
    
    if (!response.ok) {
      const errorMessage = typeof data === 'string'
        ? data
        : (data.message || `API request failed with status ${response.status}`);
      throw new Error(errorMessage);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API multipart POST error:', error);
    return { success: false, error: error.message };
  }
};

// Make authenticated multipart/form-data PUT request
export const apiPutFormData = async (endpoint, formData, options = {}) => {
  try {
    const API_BASE_URL = getServer();
    const headers = getAuthHeaders(options.headers);
    delete headers['Content-Type'];
    delete headers['content-type'];

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: formData,
      ...options,
    });

    if (response.status === 401) {
      throw new Error('Unauthorized - please login again');
    }

    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const errorMessage = typeof data === 'string'
        ? data
        : (data.message || `API request failed with status ${response.status}`);
      throw new Error(errorMessage);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API multipart PUT error:', error);
    return { success: false, error: error.message };
  }
};

// Make authenticated PUT request
export const apiPut = async (endpoint, body, options = {}) => {
  try {
    const API_BASE_URL = getServer();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(options.headers),
      body: JSON.stringify(body),
      ...options,
    });

    if (response.status === 401) {
      throw new Error('Unauthorized - please login again');
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `API request failed with status ${response.status}`);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API PUT error:', error);
    return { success: false, error: error.message };
  }
};

// Make authenticated DELETE request
export const apiDelete = async (endpoint, options = {}) => {
  try {
    const API_BASE_URL = getServer();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders(options.headers),
      ...options,
    });

    if (response.status === 401) {
      throw new Error('Unauthorized - please login again');
    }

    const data = await response.json().catch(() => ({}));
    
    if (!response.ok) {
      throw new Error(data.message || `API request failed with status ${response.status}`);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API DELETE error:', error);
    return { success: false, error: error.message };
  }
};
